import React, { useState } from 'react';
import Image from '@components/AppImage';
import Icon from '@components/AppIcon';

const DoctorProfileModal = ({ doctor, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !doctor) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'User' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="relative h-48 bg-gradient-to-r from-[#08005f] to-[#08005f]">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition"
            aria-label="Close profile"
          >
            <Icon name="X" size={20} color="white" />
          </button>

          <div className="absolute bottom-6 left-6 flex items-end space-x-4">
            <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-lg">
              <Image
                src={doctor.image}
                alt={doctor.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/400x400?text=Doctor+Image';
                }}
              />
            </div>
            <div className="text-white pb-2">
              <h2 className="font-heading font-bold text-2xl mb-1">
                {doctor.name}
              </h2>
              <p className="text-white/90 font-medium">{doctor.specialty}</p>
              <p className="text-white/80 text-sm">{doctor.title}</p>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b">
          <div className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 border-b-2 transition ${
                  activeTab === tab.id
                    ? 'border-[#eb2227] text-[#08005f] font-semibold'
                    : 'border-transparent text-gray-500 hover:text-[#08005f]'
                }`}
              >
                <Icon
                  name={tab.icon}
                  size={16}
                  className={activeTab === tab.id ? 'text-[#eb2227]' : 'text-gray-400'}
                />
                <span className="text-sm">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-4 gap-4">
                {doctor.experience > 0 && (
                  <div className="text-center p-4 bg-[#f5f6fa] rounded-lg">
                    <div className="font-heading font-bold text-xl text-[#08005f]">
                      {doctor.experience}+
                    </div>
                    <div className="text-gray-600 text-sm">Years Experience</div>
                  </div>
                )}
                {doctor.patientsServed > 0 && (
                  <div className="text-center p-4 bg-[#f5f6fa] rounded-lg">
                    <div className="font-heading font-bold text-xl text-[#08005f]">
                      {doctor.patientsServed}+
                    </div>
                    <div className="text-gray-600 text-sm">Patients Served</div>
                  </div>
                )}
                {doctor.successRate > 0 && (
                  <div className="text-center p-4 bg-[#f5f6fa] rounded-lg">
                    <div className="font-heading font-bold text-xl text-[#eb2227]">
                      {doctor.successRate}%
                    </div>
                    <div className="text-gray-600 text-sm">Success Rate</div>
                  </div>
                )}
                {doctor.rating > 0 && (
                  <div className="text-center p-4 bg-[#f5f6fa] rounded-lg">
                    <div className="font-heading font-bold text-xl text-[#08005f]">
                      {doctor.rating}
                    </div>
                    <div className="text-gray-600 text-sm">Patient Rating</div>
                  </div>
                )}
              </div>

              {/* About */}
              {doctor.about && (
                <div>
                  <h3 className="font-heading font-semibold text-lg text-[#08005f] mb-3">
                    About {doctor.name}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{doctor.about}</p>
                </div>
              )}

              {/* Specializations */}
              {doctor.specializations && doctor.specializations.length > 0 && (
                <div>
                  <h3 className="font-heading font-semibold text-lg text-[#08005f] mb-3">
                    Areas of Expertise
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {doctor.specializations.map((spec, index) => (
                      <span
                        key={index}
                        className="bg-[#eb2227]/10 text-[#eb2227] px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Languages */}
              {doctor.languages && doctor.languages.length > 0 && (
                <div>
                  <h3 className="font-heading font-semibold text-lg text-[#08005f] mb-3">
                    Languages Spoken
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {doctor.languages.map((language, index) => (
                      <span
                        key={index}
                        className="bg-[#08005f]/10 text-[#08005f] px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {language}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Credentials */}
              {doctor.credentials && doctor.credentials.length > 0 && (
                <div>
                  <h3 className="font-heading font-semibold text-lg text-[#08005f] mb-3">
                    Credentials & Training
                  </h3>
                  <ul className="space-y-2">
                    {doctor.credentials.map((credential, index) => (
                      <li key={index} className="text-gray-600 flex items-start">
                        <Icon name="GraduationCap" size={16} className="mr-2 mt-0.5 text-[#eb2227]" />
                        {credential}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoctorProfileModal;